import { RootPage } from '../page';
import $api from '../../api';
import CreateTour from './createTour';
import AppearanceSettingsView from './appearanceSettingsView';
import { TourDesign } from '../../models/Gen/TrackableBaseModels';
import { TourDescriptor } from '../../site-config/config';
import languages from '../../utils/languages';
import Enumerable from 'linq';
import { getLocalSettingsOrDefaultAutoObservable, getLocalSettingsOrDefaultAutoObservableArray } from '../../utils/localSettings';
import { openEditor } from '../../common/editorUtils';
import { fire } from '../../utils/events';
import CopyToDialog from '../../components/copyToDialog';

class ToursOverviewView extends RootPage {
    constructor() {
        super();

        CopyToDialog();

        this.deferUpdates = true;

        this.isDesignDirty = ko.observable(false);

        this.tours = ko.observableArray();
        this.tourDesign = ko.observable();

        this.setActiveTab('cards');

        this.groupByTag = getLocalSettingsOrDefaultAutoObservable('tourslist_filter_grouping', false);

        this.filters = getLocalSettingsOrDefaultAutoObservableArray('tourslist_filter_pushstate', []);
        this.filteredTours = ko.computed(() => {
            const appliedFilters = this.filters();
            let toursToReturn = this.tours();

            if (appliedFilters.length > 0) {
                toursToReturn = toursToReturn.filter(i => appliedFilters.indexOf(i.publishStatus) !== -1);
            }

            if (this.groupByTag()) {
                toursToReturn = Enumerable.from(toursToReturn)
                    .select(i => {
                        if (!i.groupName) i.groupName = ko.observable();
                        i.groupName(null);

                        return i;
                    })
                    .orderBy(i => !i.tag ? String.fromCharCode(Number.MAX_SAFE_INTEGER) : i.tag)
                    .thenBy(i => (i.name || '').toLowerCase())
                    .toArray();

                // set tags only to first of group - because of simplier UI rendering
                if (toursToReturn.length > 0) {
                    let prevTag = toursToReturn[0].tag;
                    toursToReturn[0].groupName(prevTag);
                    for (let t of toursToReturn) {
                        const stdTag = t.tag || 'No tag';
                        if (prevTag != stdTag) {
                            prevTag = stdTag;
                            t.groupName(stdTag);
                        }
                    }
                }
            }
            return toursToReturn;
        });
    }

    onInit() {
        this.tours(this.getServerSideData('tours').items);
        this.topHelpShown(/*auth.user.ConfigObject.ToursHelpShown !== true*/false); // bug - switched true/false

        this.tourDesign(new TourDesign(this.getServerSideData('tourDesign')));

        AppearanceSettingsView({ tourDesign: this.tourDesign, parentPage: this, isDirty: this.isDesignDirty });
    }

    saveDesign() {
        fire('save_design');
    }

    setFilter(filter) {
        if (this.filters.indexOf(filter) < 0) {
            this.filters.push(filter);
        } else {
            this.filters.remove(filter);
        }
    }

    isFilterActive(filter) {
        return ko.computed(() => {
            return this.filters.indexOf(filter) !== -1;
        });
    }

    createTour() {
        this.showDialog(CreateTour(this));
    }

    goToTour(id) {
        window.location.href = '/' + auth.project.UniqueCode + '/tours/detail/' + id;
    }

    editTour(tour, e) {
        openEditor('tour', tour.baseUrl, tour.id, () => {
            this.refreshTour(tour);
        });

        TRACK_EVENT('Tours', 'Editor opened', { from: 'list' });
    }

    refreshTour(tour) {
        $api.tours.get(
            { projectCode: auth.project.UniqueCode, id: tour.id },
            {},
            resp => {
                if (tour.lastUpdatedAt < resp.lastUpdatedAt) {
                    TRACK_EVENT('Tours', 'Saved');
                }

                this.tours.replace(tour, resp);
            }
        );
    }

    getTours() {
        $api.tours.getList(
            { projectCode: auth.project.UniqueCode },
            {},
            (resp) => {
                this.tours(resp.items);
            }
        );
    }

    copyTo(tour) {
        this.showDialog(CopyToDialog(this), {
            root: tour,
            callback: (projId) => {
                $api.tours.duplicate(
                    { projectCode: auth.project.UniqueCode, id: ko.unwrap(tour.id), targetProjectId: projId },
                    { },
                    (resp) => {
                        this.showToast('Tour was copied to the selected workspace.');

                        this.closeDialog();
                    }
                );
            }
        });
    }

    duplicateTour(tour) {
        this.startSubmitting('tourDuplication');
        $api.tours.duplicate(
            { projectCode: auth.project.UniqueCode, id: ko.unwrap(tour.id) },
            {},
            (resp) => {
                this.stopSubmitting('tourDuplication');
                this.showToast('Tour was cloned.');

                this.getTours();
            }
        );
    }

    deleteTour(tour) {
        this.confirmStandardDialog('Do you really want to delete this tour?', {
            success: () => {
                this.startSubmitting('delete');

                $api.tours.delete(
                    { projectCode: auth.project.UniqueCode, id: ko.unwrap(tour.id) },
                    {},
                    () => {
                        this.stopSubmitting('delete');
                        window.location.href = '/' + auth.project.UniqueCode + '/tours/overview';
                    }
                );
            }
        });
    }

    publishTour(tour) {
        $api.tours.publish(
            { projectCode: auth.project.UniqueCode, id: tour.id },
            {},
            resp => {
                this.refreshTour(tour);
            }
        );
    }

    unpublishTour(tour) {
        $api.tours.unpublish(
            { projectCode: auth.project.UniqueCode, id: tour.id },
            {},
            resp => {
                this.refreshTour(tour);
            }
        );
    }
}

export default ToursOverviewView;